<template>
  <div
    ref="wrap"
    class="tsf"
  >
    <!-- Chip trigger -->
    <button
      class="tsf-chip"
      :class="{ 'tsf-chip--open': open }"
      type="button"
      @click="toggleOpen"
    >
      <feather-icon
        icon="CalendarIcon"
        size="12"
        class="tsf-chip-icon"
      />
      <span>{{ chipLabel }}</span>
      <span
        v-if="activeKey !== 'all-time'"
        class="tsf-chip-remove"
        @click.stop="clearTimespan"
      >×</span>
      <feather-icon
        v-else
        icon="ChevronDownIcon"
        size="11"
        class="tsf-caret"
      />
    </button>

    <!-- Dropdown panel -->
    <div
      v-if="open"
      class="tsf-panel"
      :class="panelWidthClass"
    >

      <!-- ── MAIN MENU ─────────────────────────────── -->
      <template v-if="panel === 'main'">
        <template v-for="(o, idx) in OPTS">
          <div
            v-if="!o"
            :key="'sep' + idx"
            class="tsf-rule"
          />
          <button
            v-else
            :key="o.key"
            class="tsf-row"
            :class="{ 'tsf-row--on': activeKey === o.key }"
            type="button"
            @click="pick(o)"
          >
            <span>{{ o.label }}</span>
            <feather-icon
              v-if="o.panel"
              icon="ChevronRightIcon"
              size="11"
            />
          </button>
        </template>
      </template>

      <!-- ── RELATIVE DATE RANGE ───────────────────── -->
      <template v-if="panel === 'relative'">
        <div class="tsf-sub-header">
          <button
            class="tsf-back"
            type="button"
            @click="panel = 'main'"
          >
            <feather-icon
              icon="ChevronLeftIcon"
              size="13"
            />
          </button>
          <div class="tsf-tabs">
            <button
              v-for="t in ['Previous', 'Current', 'Next']"
              :key="t"
              class="tsf-tab"
              :class="{ 'tsf-tab--on': relTab === t }"
              type="button"
              @click="relTab = t"
            >{{ t }}</button>
          </div>
        </div>

        <!-- Previous -->
        <div
          v-if="relTab === 'Previous'"
          class="tsf-body"
        >
          <div class="tsf-num-row">
            <input
              v-model.number="prevN"
              type="number"
              min="1"
              class="tsf-num"
            >
            <select
              v-model="prevUnit"
              class="tsf-sel"
            >
              <option
                v-for="u in UNITS"
                :key="u"
              >
                {{ u }}
              </option>
            </select>
          </div>
          <div
            class="tsf-toggle-row"
            @click="inclPrev = !inclPrev"
          >
            <span
              class="tsf-toggle"
              :class="{ on: inclPrev }"
            ><span class="tsf-thumb" /></span>
            <span>{{ inclLabel }}</span>
          </div>
          <p class="tsf-preview">
            <feather-icon
              icon="CalendarIcon"
              size="11"
            />
            {{ prevPreview }}
          </p>
          <button
            class="tsf-apply-btn"
            type="button"
            @click="applyPrev"
          >
            Add filter
          </button>
        </div>

        <!-- Current -->
        <div
          v-if="relTab === 'Current'"
          class="tsf-body"
        >
          <div class="tsf-cur-grid">
            <button
              v-for="cu in CUR_UNITS"
              :key="cu.key"
              class="tsf-cur-btn"
              :class="{ on: curUnit === cu.key }"
              type="button"
              @click="applyCur(cu)"
            >{{ cu.label }}</button>
          </div>
        </div>

        <!-- Next -->
        <div
          v-if="relTab === 'Next'"
          class="tsf-body"
        >
          <div class="tsf-num-row">
            <input
              v-model.number="nxtN"
              type="number"
              min="1"
              class="tsf-num"
            >
            <select
              v-model="nxtUnit"
              class="tsf-sel"
            >
              <option
                v-for="u in UNITS"
                :key="u"
              >
                {{ u }}
              </option>
            </select>
          </div>
          <div
            class="tsf-toggle-row"
            @click="inclNxt = !inclNxt"
          >
            <span
              class="tsf-toggle"
              :class="{ on: inclNxt }"
            ><span class="tsf-thumb" /></span>
            <span>Include today</span>
          </div>
          <p class="tsf-preview">
            <feather-icon
              icon="CalendarIcon"
              size="11"
            />
            {{ nxtPreview }}
          </p>
          <button
            class="tsf-apply-btn"
            type="button"
            @click="applyNxt"
          >
            Add filter
          </button>
        </div>
      </template>

      <!-- ── FIXED DATE RANGE ──────────────────────── -->
      <template v-if="panel === 'fixed'">
        <div class="tsf-sub-header">
          <button
            class="tsf-back"
            type="button"
            @click="panel = 'main'"
          >
            <feather-icon
              icon="ChevronLeftIcon"
              size="13"
            />
          </button>
          <div class="tsf-tabs">
            <button
              v-for="t in FIXED_TABS"
              :key="t"
              class="tsf-tab"
              :class="{ 'tsf-tab--on': fixedTab === t }"
              type="button"
              @click="switchFixed(t)"
            >{{ t }}</button>
          </div>
        </div>
        <div class="tsf-body tsf-body--cal">
          <!-- Date inputs above calendar -->
          <div class="tsf-date-inputs">
            <input
              class="tsf-date-inp"
              :value="fixedFromDisplay"
              readonly
              placeholder="Pick a date"
            >
            <template v-if="fixedTab === 'Between'">
              <span class="tsf-and">and</span>
              <input
                class="tsf-date-inp"
                :value="fixedToDisplay"
                readonly
                placeholder="Pick a date"
              >
            </template>
          </div>
          <!-- Inline calendar – key forces re-init on tab change -->
          <flat-pickr
            :key="fixedTab"
            v-model="fixedVal"
            :config="fixedCal"
            @on-change="onCalChange"
          />
          <!-- Footer -->
          <div class="tsf-fixed-footer">
            <button
              class="tsf-add-time-btn"
              type="button"
            >
              <feather-icon
                icon="ClockIcon"
                size="11"
              />
              Add time
            </button>
            <button
              class="tsf-apply-btn tsf-apply-btn--sm"
              type="button"
              :disabled="!canApplyFixed"
              @click="applyFixed"
            >Add filter</button>
          </div>
        </div>
      </template>

      <!-- ── EXCLUDE ───────────────────────────────── -->
      <template v-if="panel === 'exclude'">

        <!-- Exclude top menu -->
        <template v-if="!excludeSub">
          <div class="tsf-sub-header tsf-sub-header--plain">
            <button
              class="tsf-back"
              type="button"
              @click="panel = 'main'"
            >
              <feather-icon
                icon="ChevronLeftIcon"
                size="13"
              />
            </button>
            <span class="tsf-sub-title">Exclude...</span>
          </div>
          <button
            v-for="e in EXCL_MENU"
            :key="e.key"
            class="tsf-row"
            type="button"
            @click="openExclSub(e.key)"
          >
            <span>{{ e.label }}</span>
            <feather-icon
              icon="ChevronRightIcon"
              size="11"
            />
          </button>
        </template>

        <!-- Exclude sub-panel (Days / Months / Quarters / Hours) -->
        <template v-else>
          <div class="tsf-sub-header tsf-sub-header--plain">
            <button
              class="tsf-back"
              type="button"
              @click="excludeSub = null"
            >
              <feather-icon
                icon="ChevronLeftIcon"
                size="13"
              />
            </button>
            <span class="tsf-sub-title">{{ currentExclTitle }}</span>
          </div>
          <div class="tsf-excl-body">
            <label class="tsf-excl-item tsf-excl-item--all">
              <input
                type="checkbox"
                :checked="isAllExclSelected"
                @change="toggleAllExcl"
              >
              Select all
            </label>
            <div
              class="tsf-excl-grid"
              :class="{ 'tsf-excl-grid--2col': currentExclItems.length > 7 }"
            >
              <label
                v-for="item in currentExclItems"
                :key="item"
                class="tsf-excl-item"
              >
                <input
                  v-model="excludeSels[excludeSub]"
                  type="checkbox"
                  :value="item"
                >
                {{ item }}
              </label>
            </div>
            <button
              class="tsf-apply-btn"
              type="button"
              @click="closeExclude"
            >
              Add filter
            </button>
          </div>
        </template>

      </template>

    </div>
  </div>
</template>

<script>
import flatPickr from 'vue-flatpickr-component'
import moment from 'moment'

/* ── static option lists ── */
const OPTS = [
  { key: 'all-time', label: 'All time' },
  null,
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'prev-week', label: 'Previous week' },
  { key: 'prev-7d', label: 'Previous 7 days' },
  { key: 'prev-30d', label: 'Previous 30 days' },
  null,
  { key: 'prev-month', label: 'Previous month' },
  { key: 'prev-3m', label: 'Previous 3 months' },
  { key: 'prev-12m', label: 'Previous 12 months' },
  null,
  { key: 'fixed', label: 'Fixed date range...', panel: 'fixed' },
  { key: 'relative', label: 'Relative date range...', panel: 'relative' },
  { key: 'exclude', label: 'Exclude...', panel: 'exclude' },
]

const UNITS = ['days', 'weeks', 'months', 'quarters', 'years']
const FIXED_TABS = ['Between', 'Before', 'On', 'After']
const CUR_UNITS = [
  { key: 'day', label: 'Day' },
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
  { key: 'quarter', label: 'Quarter' },
  { key: 'year', label: 'Year' },
]

const EXCL_MENU = [
  { key: 'days', label: 'Days of the week...' },
  { key: 'months', label: 'Months of the year...' },
  { key: 'quarters', label: 'Quarters of the year...' },
  { key: 'hours', label: 'Hours of the day...' },
]

const EXCL_DATA = {
  days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
  months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
  quarters: ['1st', '2nd', '3rd', '4th'],
  hours: [
    '12 AM', '1 AM', '2 AM', '3 AM', '4 AM', '5 AM', '6 AM', '7 AM', '8 AM', '9 AM', '10 AM', '11 AM',
    '12 PM', '1 PM', '2 PM', '3 PM', '4 PM', '5 PM', '6 PM', '7 PM', '8 PM', '9 PM', '10 PM', '11 PM',
  ],
}

const DEFAULT_KEY = 'prev-3m'

/* ── date helpers ── */
const fmt = d => d.format('YYYY-MM-DD')
function fmtDate(d) { return moment(d).format('MMM D, YYYY') }
function fmtFull(d) { return moment(d).format('MMMM D, YYYY') }

const PRESET_BUILDERS = {
  today: () => { const d = fmt(moment()); return { start: d, end: d, label: 'Today' } },
  yesterday: () => { const d = fmt(moment().subtract(1, 'd')); return { start: d, end: d, label: 'Yesterday' } },
  'prev-week': () => ({
    start: fmt(moment().subtract(1, 'w').startOf('isoWeek')),
    end: fmt(moment().subtract(1, 'w').endOf('isoWeek')),
    label: 'Previous week',
  }),
  'prev-7d': () => ({ start: fmt(moment().subtract(7, 'd')), end: fmt(moment()), label: 'Previous 7 days' }),
  'prev-30d': () => ({ start: fmt(moment().subtract(30, 'd')), end: fmt(moment()), label: 'Previous 30 days' }),
  'prev-month': () => ({
    start: fmt(moment().subtract(1, 'M').startOf('month')),
    end: fmt(moment().subtract(1, 'M').endOf('month')),
    label: 'Previous month',
  }),
  'prev-3m': () => ({
    start: fmt(moment().subtract(3, 'M').startOf('month')),
    end: fmt(moment().subtract(1, 'M').endOf('month')),
    label: 'Previous 3 months',
  }),
  'prev-12m': () => ({
    start: fmt(moment().subtract(12, 'M').startOf('month')),
    end: fmt(moment().subtract(1, 'M').endOf('month')),
    label: 'Previous 12 months',
  }),
}

function presetDates(key) {
  const builder = PRESET_BUILDERS[key]
  return builder ? builder() : null
}

function periodKey(unit) {
  if (unit === 'quarters') return 'quarter'
  if (unit === 'years') return 'year'
  return 'month'
}

function resolveFixedRange(tab, from, to) {
  if (tab === 'Between') {
    return { start: from, end: to, label: `${fmtDate(from)} – ${fmtDate(to)}` }
  }
  if (tab === 'Before') {
    return { start: '2000-01-01', end: from, label: `Before ${fmtDate(from)}` }
  }
  if (tab === 'On') {
    return { start: from, end: from, label: `On ${fmtDate(from)}` }
  }
  return { start: from, end: fmt(moment().add(5, 'years')), label: `After ${fmtDate(from)}` }
}

function parseBetweenDates(dates) {
  if (dates.length === 2) {
    return { from: moment(dates[0]).format('YYYY-MM-DD'), to: moment(dates[1]).format('YYYY-MM-DD') }
  }
  return { from: moment(dates[0]).format('YYYY-MM-DD'), to: null }
}

function calcPrevPeriodDates(n, unit, incl) {
  const pk = periodKey(unit)
  const start = moment().subtract(n, unit).startOf(pk)
  const end = incl ? moment().endOf(pk) : moment().subtract(1, pk).endOf(pk)
  return { start: fmt(start), end: fmt(end) }
}

function calcPrevDaysDates(n, unit, incl) {
  const start = moment().subtract(n, unit)
  const end = incl ? moment() : moment().subtract(1, 'd')
  return { start: fmt(start), end: fmt(end) }
}

function calcPrevDates(n, unit, incl) {
  const safeN = (Number.isFinite(n) && n > 0) ? n : 1
  if (['months', 'quarters', 'years'].includes(unit)) {
    return calcPrevPeriodDates(safeN, unit, incl)
  }
  return calcPrevDaysDates(safeN, unit, incl)
}

function calcNxtDates(n, unit, incl) {
  const safeN = (Number.isFinite(n) && n > 0) ? n : 1
  const start = incl ? moment() : moment().add(1, 'd')
  const end = moment().add(safeN, unit)
  return { start: fmt(start), end: fmt(end) }
}

function calcCurDates(unitKey) {
  return { start: fmt(moment().startOf(unitKey)), end: fmt(moment().endOf(unitKey)) }
}

function pluralUnit(n, unit) { return n === 1 ? unit.replace(/s$/, '') : unit }

export default {
  name: 'AnalyticsTimespanFilter',
  components: { flatPickr },
  data() {
    const def = presetDates(DEFAULT_KEY)
    return {
      OPTS,
      UNITS,
      FIXED_TABS,
      CUR_UNITS,
      EXCL_MENU,
      open: false,
      panel: 'main',
      activeKey: DEFAULT_KEY,
      activeLabel: def.label,

      // Relative – Previous
      relTab: 'Previous',
      prevN: 30,
      prevUnit: 'days',
      inclPrev: false,

      // Relative – Next
      nxtN: 30,
      nxtUnit: 'days',
      inclNxt: false,

      // Relative – Current
      curUnit: null,

      // Fixed
      fixedTab: 'Between',
      fixedVal: null,
      fixedFrom: null,
      fixedTo: null,

      // Exclude
      excludeSub: null,
      excludeSels: {
 days: [], months: [], quarters: [], hours: [],
},

      // Track last-applied date range so closeExclude can emit it
      appliedStart: def.start,
      appliedEnd: def.end,
    }
  },
  computed: {
    chipLabel() {
      return this.activeKey === 'all-time' ? 'All time' : `Timespan: ${this.activeLabel}`
    },

    panelWidthClass() {
      if (this.panel === 'fixed' && this.fixedTab === 'Between') return 'tsf-panel--xl'
      if (this.panel === 'fixed') return 'tsf-panel--md'
      if (this.panel === 'exclude' && this.excludeSub) return 'tsf-panel--md'
      return ''
    },

    inclLabel() {
      const map = { months: 'Include this month', quarters: 'Include this quarter', years: 'Include this year' }
      return map[this.prevUnit] || 'Include today'
    },

    prevPreview() {
      const { start, end } = calcPrevDates(this.prevN, this.prevUnit, this.inclPrev)
      return `${fmtDate(start)} – ${fmtDate(end)}`
    },

    nxtPreview() {
      const { start, end } = calcNxtDates(this.nxtN, this.nxtUnit, this.inclNxt)
      return `${fmtDate(start)} – ${fmtDate(end)}`
    },

    fixedFromDisplay() { return this.fixedFrom ? fmtFull(this.fixedFrom) : '' },
    fixedToDisplay() { return this.fixedTo ? fmtFull(this.fixedTo) : '' },

    fixedCal() {
      return {
        mode: this.fixedTab === 'Between' ? 'range' : 'single',
        inline: true,
        dateFormat: 'Y-m-d',
        showMonths: this.fixedTab === 'Between' ? 2 : 1,
      }
    },

    canApplyFixed() {
      return this.fixedTab === 'Between' ? !!(this.fixedFrom && this.fixedTo) : !!this.fixedFrom
    },

    currentExclTitle() {
      const entry = EXCL_MENU.find(e => e.key === this.excludeSub)
      return entry ? entry.label : ''
    },

    currentExclItems() { return EXCL_DATA[this.excludeSub] || [] },

    isAllExclSelected() {
      const items = this.currentExclItems
      return items.length > 0 && this.excludeSels[this.excludeSub]?.length === items.length
    },
  },
  mounted() {
    const def = presetDates(DEFAULT_KEY)
    this.$emit('change', { startDate: def.start, endDate: def.end })
    document.addEventListener('mousedown', this.onDocClick)
  },
  beforeDestroy() {
    document.removeEventListener('mousedown', this.onDocClick)
  },
  methods: {
    onDocClick(e) {
      if (!this.$refs.wrap.contains(e.target)) this.open = false
    },
    toggleOpen() {
      this.open = !this.open
      if (this.open) this.panel = 'main'
    },

    pick(opt) {
      if (opt.panel) { this.panel = opt.panel; return }
      if (opt.key === 'all-time') { this.clearTimespan(); return }
      const d = presetDates(opt.key)
      if (!d) return
      this.activeKey = opt.key
      this.activeLabel = d.label
      this.appliedStart = d.start
      this.appliedEnd = d.end
      this.open = false
      this.$emit('change', { startDate: d.start, endDate: d.end })
    },

    clearTimespan() {
      this.activeKey = 'all-time'
      this.activeLabel = 'All time'
      this.appliedStart = null
      this.appliedEnd = null
      this.open = false
      this.$emit('change', { startDate: null, endDate: null })
    },

    /* Relative – Previous */
    applyPrev() {
      const { start, end } = calcPrevDates(this.prevN, this.prevUnit, this.inclPrev)
      this.activeKey = 'relative'
      this.activeLabel = `Previous ${this.prevN} ${pluralUnit(this.prevN, this.prevUnit)}`
      this.appliedStart = start
      this.appliedEnd = end
      this.open = false
      this.$emit('change', { startDate: start, endDate: end })
    },

    /* Relative – Current */
    applyCur(cu) {
      this.curUnit = cu.key
      const { start, end } = calcCurDates(cu.key)
      this.activeKey = 'current'
      this.activeLabel = `Current ${cu.label.toLowerCase()}`
      this.appliedStart = start
      this.appliedEnd = end
      this.open = false
      this.$emit('change', { startDate: start, endDate: end })
    },

    /* Relative – Next */
    applyNxt() {
      const { start, end } = calcNxtDates(this.nxtN, this.nxtUnit, this.inclNxt)
      this.activeKey = 'next'
      this.activeLabel = `Next ${this.nxtN} ${pluralUnit(this.nxtN, this.nxtUnit)}`
      this.appliedStart = start
      this.appliedEnd = end
      this.open = false
      this.$emit('change', { startDate: start, endDate: end })
    },

    /* Fixed */
    switchFixed(tab) {
      this.fixedTab = tab
      this.fixedVal = null
      this.fixedFrom = null
      this.fixedTo = null
    },
    onCalChange(dates) {
      if (!dates.length) return
      if (this.fixedTab === 'Between') {
        const { from, to } = parseBetweenDates(dates)
        this.fixedFrom = from
        this.fixedTo = to
      } else {
        this.fixedFrom = moment(dates[0]).format('YYYY-MM-DD')
        this.fixedTo = null
      }
    },
    applyFixed() {
      if (!this.fixedFrom) return
      if (this.fixedTab === 'Between' && !this.fixedTo) return
      const { start, end, label } = resolveFixedRange(this.fixedTab, this.fixedFrom, this.fixedTo)
      this.activeKey = 'fixed'
      this.activeLabel = label
      this.appliedStart = start
      this.appliedEnd = end
      this.open = false
      this.$emit('change', { startDate: start, endDate: end })
    },

    /* Exclude */
    openExclSub(key) {
      this.excludeSub = key
      this.excludeSels[key] = []
    },
    toggleAllExcl(e) {
      this.excludeSels[this.excludeSub] = e.target.checked ? [...this.currentExclItems] : []
    },
    closeExclude() {
      this.open = false
      this.excludeSub = null
      // Exclude filtering is captured in UI state (excludeSels) but not emitted
      // because the backend aggregation pipeline does not yet support it.
      // Emit only the current date range so the parent filter stays consistent.
      this.$emit('change', { startDate: this.appliedStart, endDate: this.appliedEnd })
    },

    /* Reset (called by parent) */
    reset() {
      const def = presetDates(DEFAULT_KEY)
      this.activeKey = DEFAULT_KEY
      this.activeLabel = def.label
      this.appliedStart = def.start
      this.appliedEnd = def.end
      this.open = false
      this.$emit('change', { startDate: def.start, endDate: def.end })
    },
  },
}
</script>

<style lang="scss">
/* Non-scoped: flatpickr base CSS must be global so the inline calendar is styled */
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>

<style lang="scss" scoped>
.tsf {
  position: relative;
  display: inline-block;
}

/* ── Chip trigger ── */
.tsf-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 32px;
  padding: 0 12px;
  border-radius: 6px;
  border: 1px solid #dbeafe;
  background: #f0f7ff;
  color: #1e40af;
  font-size: 0.78rem;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s, border-color 0.15s;

  &:hover,
  &--open { background: #dbeafe; border-color: #93c5fd; }

  .tsf-caret { opacity: 0.6; }
}

.tsf-chip-remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  font-size: 0.82rem;
  line-height: 1;
  color: #1e40af;
  opacity: 0.65;
  transition: opacity 0.15s, background 0.15s;

  &:hover {
    opacity: 1;
    background: rgba(30, 64, 175, 0.12);
  }
}

/* ── Panel ── */
.tsf-panel {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 9999;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  min-width: 220px;
  max-height: 540px;
  overflow-y: auto;

  &--md { min-width: 300px; }
  &--xl { min-width: 650px; max-height: none; overflow: visible; }
}

/* ── Main menu rows ── */
.tsf-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 8px 14px;
  background: transparent;
  border: none;
  font-size: 0.82rem;
  color: #1e293b;
  cursor: pointer;
  text-align: left;
  gap: 8px;
  transition: background 0.1s;

  &:hover { background: #f1f5f9; }
  &--on { color: #1e40af; font-weight: 600; background: #eff6ff; }
}

.tsf-rule { height: 1px; background: #f0f4f8; margin: 4px 0; }

/* ── Sub-panel headers ── */
.tsf-sub-header {
  display: flex;
  align-items: center;
  padding: 8px 10px 6px;
  border-bottom: 1px solid #f0f4f8;
  gap: 4px;

  &--plain {
    .tsf-sub-title { font-size: 0.8rem; font-weight: 600; color: #374151; }
  }
}

.tsf-back {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 4px;
  color: #64748b;
  flex-shrink: 0;
  &:hover { background: #f1f5f9; color: #1e40af; }
}

/* ── Tabs (Previous/Current/Next  &  Between/Before/On/After) ── */
.tsf-tabs { display: flex; gap: 2px; flex: 1; }

.tsf-tab {
  flex: 1;
  padding: 4px 6px;
  border: none;
  background: transparent;
  border-radius: 5px;
  font-size: 0.78rem;
  font-weight: 500;
  color: #64748b;
  cursor: pointer;
  text-align: center;
  transition: background 0.1s;
  &:hover { background: #f1f5f9; }
  &--on { background: #eff6ff; color: #1e40af; font-weight: 600; }
}

/* ── Body ── */
.tsf-body {
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;

  &--cal {
    padding: 10px;
    gap: 8px;

    // Flatten flatpickr inline calendar (::v-deep required for Vue 2 scoped styles)
    ::v-deep .flatpickr-calendar {
      box-shadow: none !important;
      border: none !important;
      background: transparent !important;
      width: auto !important;
    }
    ::v-deep .flatpickr-input { display: none !important; }
    ::v-deep .flatpickr-months { padding: 0 4px; }
  }
}

/* ── Number + unit ── */
.tsf-num-row { display: flex; gap: 6px; }

.tsf-num {
  width: 64px;
  padding: 5px 8px;
  border: 1px solid #d1d5db;
  border-radius: 5px;
  font-size: 0.82rem;
  color: #1e293b;
  text-align: center;
  outline: none;
  &:focus { border-color: #3b82f6; }
}

.tsf-sel {
  flex: 1;
  padding: 5px 8px;
  border: 1px solid #d1d5db;
  border-radius: 5px;
  font-size: 0.82rem;
  color: #1e293b;
  background: #fff;
  outline: none;
  cursor: pointer;
  &:focus { border-color: #3b82f6; }
}

/* ── Toggle ── */
.tsf-toggle-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8rem;
  color: #374151;
  cursor: pointer;
  user-select: none;
}

.tsf-toggle {
  position: relative;
  width: 32px;
  height: 18px;
  background: #d1d5db;
  border-radius: 9px;
  transition: background 0.2s;
  flex-shrink: 0;
  &.on { background: #3b82f6; }
}

.tsf-thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 14px;
  height: 14px;
  background: #fff;
  border-radius: 50%;
  transition: transform 0.2s;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
  .tsf-toggle.on & { transform: translateX(14px); }
}

/* ── Preview row ── */
.tsf-preview {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.78rem;
  color: #64748b;
  margin: 0;
  padding: 6px 8px;
  background: #f8fafc;
  border-radius: 5px;
}

/* ── Apply / Add filter button ── */
.tsf-apply-btn {
  width: 100%;
  padding: 7px;
  background: #1e40af;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
  &:hover { background: #1d4ed8; }
  &:disabled { background: #93c5fd; cursor: not-allowed; }
  &--sm { width: auto; padding: 6px 14px; }
}

/* ── Current tab grid ── */
.tsf-cur-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 6px; }

.tsf-cur-btn {
  padding: 7px 4px;
  border: 1px solid #e2e8f0;
  border-radius: 5px;
  background: #f8fafc;
  color: #374151;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  text-align: center;
  transition: background 0.1s, border-color 0.1s;
  &:hover { background: #eff6ff; border-color: #93c5fd; }
  &.on { background: #eff6ff; border-color: #3b82f6; color: #1e40af; font-weight: 600; }
}

/* ── Fixed date range ── */
.tsf-date-inputs {
  display: flex;
  align-items: center;
  gap: 6px;
}

.tsf-date-inp {
  flex: 1;
  padding: 5px 8px;
  border: 1px solid #d1d5db;
  border-radius: 5px;
  font-size: 0.8rem;
  color: #1e293b;
  background: #fff;
  outline: none;
  cursor: default;
  min-width: 0;
}

.tsf-and { font-size: 0.78rem; color: #64748b; white-space: nowrap; }

.tsf-fixed-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 4px;
}

.tsf-add-time-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 8px;
  border: none;
  background: transparent;
  font-size: 0.78rem;
  color: #64748b;
  cursor: pointer;
  border-radius: 4px;
  &:hover { color: #1e40af; background: #eff6ff; }
}

/* ── Exclude sub-panels ── */
.tsf-excl-body {
  padding: 8px 12px 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.tsf-excl-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.82rem;
  color: #1e293b;
  cursor: pointer;
  padding: 3px 0;
  user-select: none;

  input[type='checkbox'] { width: 14px; height: 14px; cursor: pointer; accent-color: #1e40af; }

  &--all {
    font-weight: 600;
    color: #374151;
    border-bottom: 1px solid #f0f4f8;
    padding-bottom: 8px;
    margin-bottom: 2px;
  }
}

.tsf-excl-grid {
  display: flex;
  flex-direction: column;
  gap: 2px;

  &--2col {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 2px 12px;
  }
}
</style>
